import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AUTH_CHANGED_EVENT,
  clearAuthSession,
  fetchUsuarioMe,
  getStoredUser,
  getToken,
  isTokenExpired,
  persistUserSession,
} from '../../utils/auth';

const AuthContext = createContext();

const syncUserState = (setters) => {
  const user = getStoredUser();
  setters.setAuthToken(user.token);
  setters.setUserRole(user.perfil);
  setters.setUserSetor(user.setor);
  setters.setUserName(user.nome);
  setters.setUserUsername(user.username);
  setters.setPermissoes(user.permissoes);
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const initial = getStoredUser();
  const [authToken, setAuthToken] = useState(initial.token);
  const [userRole, setUserRole] = useState(initial.perfil);
  const [userSetor, setUserSetor] = useState(initial.setor);
  const [userName, setUserName] = useState(initial.nome);
  const [userUsername, setUserUsername] = useState(initial.username);
  const [permissoes, setPermissoes] = useState(initial.permissoes);

  const refreshFromStorage = useCallback(() => {
    syncUserState({
      setAuthToken,
      setUserRole,
      setUserSetor,
      setUserName,
      setUserUsername,
      setPermissoes,
    });
  }, []);

  const setUser = useCallback(async (token) => {
    if (!token) {
      clearAuthSession();
      refreshFromStorage();
      return;
    }

    try {
      const me = await fetchUsuarioMe(token);
      persistUserSession(token, me);
    } catch {
      persistUserSession(token, {
        nome: '',
        username: '',
        perfil: '',
        setor: '',
        permissoes: [],
      });
    }

    refreshFromStorage();
  }, [refreshFromStorage]);

  const logout = useCallback(() => {
  window.location.href = '/'; 
  clearAuthSession();          
}, []);

  const validateToken = useCallback(() => {
    const token = getToken();
    if (!token) return;
    if (isTokenExpired(token)) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    validateToken();
    refreshFromStorage();

    const onStorage = (e) => {
      if (!e?.key || e.key === 'token' || e.key === 'permissoes') {
        refreshFromStorage();
      }
    };

    const onFocus = () => {
      validateToken();
      refreshFromStorage();
    };

    const onAuthChanged = () => refreshFromStorage();

    window.addEventListener('storage', onStorage);
    window.addEventListener('focus', onFocus);
    window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
    window.addEventListener('contracts-auth-changed', onAuthChanged);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged);
      window.removeEventListener('contracts-auth-changed', onAuthChanged);
    };
  }, [validateToken, refreshFromStorage]);

  const hasPermission = useCallback(
    (slug) => Array.isArray(permissoes) && permissoes.includes(slug),
    [permissoes]
  );

  return (
    <AuthContext.Provider
      value={{
        authToken,
        userRole,
        userSetor,
        userName,
        userUsername,
        permissoes,
        hasPermission,
        setUser,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
